import moment from 'moment'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import { requestCommunityMemberGrowthReport } from '../../../../../actions/reportsActions'
import { deserialize } from '../../../../../utility/serialization'
import { DATETIME_FORMAT, getMomentInCommunityTimezone } from '../../../../../utility/datesHelper'
import { COMMON_ERRORS } from '../../../../../misc/Constants'
import { buildCommunityYearRangeFunctions } from '../../../../../utility/communityYearRanges'

import Loader from '../../../../common/loader/Loader'
import Select from '../../../../common/form/Select'
import CheckBox from '../../../../common/form/CheckBox'
import MemberGrowthChart from './MemberGrowthChart'

import businessDashboardClasses from '../BusinessDashboard.module.scss'
import classes from './MemberGrowthWidget.module.scss'

const CHART_DATA_TYPE = {
  students: 'students',
  families: 'families',
}

const buildMemberGrowthYearRangeFunctions = (createdAt) => {
  const ranges = buildCommunityYearRangeFunctions(createdAt)
  const currentYear = moment().year()
  const baseLabel = ranges[currentYear].textFunc()

  ranges[currentYear] = {
    textFunc: () => baseLabel,
    valueFunc: () => ({
      startDate: moment()
        .subtract(11, 'months')
        .startOf('month')
        .toDate(),
      endDate: new Date(),
    }),
  }

  return ranges
}

const SingleStatistics = ({ value, topLabel, bottomLabel }) => (
  <div className={classnames('d-flex flex-column text-center', businessDashboardClasses.statistics)}>
    <span className={businessDashboardClasses.value}>{value}</span>
    <span className={businessDashboardClasses.topLabel}>{topLabel}</span>
    <span className={businessDashboardClasses.bottomLabel}>{bottomLabel}</span>
  </div>
)

SingleStatistics.propTypes = {
  value: PropTypes.number.isRequired,
  topLabel: PropTypes.string.isRequired,
  bottomLabel: PropTypes.string.isRequired,
}

const formatGrowthPercentage = (ratio) => {
  if (ratio === null || ratio === undefined) {
    return null
  }
  return Intl.NumberFormat('en-US', { style: 'percent', signDisplay: 'exceptZero', maximumFractionDigits: 1 })
    .format(ratio)
}

const MemberGrowthWidget = () => {
  const { t } = useTranslation()
  const community = useSelector(state => state.user.current_user.communities[0])
  const yearRangeFunctions = useMemo(
    () => buildMemberGrowthYearRangeFunctions(community?.created_at),
    [community?.created_at]
  )
  const yearSelectOptions = useMemo(
    () => Object.keys(yearRangeFunctions)
      .reverse()
      .map(key => {
        const { tKey, values } = yearRangeFunctions[key].textFunc()
        return { label: t(tKey, values), value: key }
      }),
    [yearRangeFunctions, t]
  )
  const defaultFilters = useMemo(() => ({
    yearOption: yearSelectOptions[0],
  }), [yearSelectOptions])

  const [filters, setFilters] = useState(defaultFilters)
  const [chartDataType, setChartDataType] = useState(CHART_DATA_TYPE.students)
  const [showOnlyNew, setShowOnlyNew] = useState(false)
  const [isLoadingChartData, setIsLoadingChartData] = useState(true)
  const [data, setData] = useState()

  useEffect(() => {
    setIsLoadingChartData(true)

    const { startDate, endDate } = yearRangeFunctions[filters.yearOption.value].valueFunc()

    requestCommunityMemberGrowthReport({
      communityId: community?.id,
      startDate: getMomentInCommunityTimezone(moment(startDate), community?.timezone, DATETIME_FORMAT)
        .toDate(),
      endDate: getMomentInCommunityTimezone(moment(endDate), community?.timezone, DATETIME_FORMAT)
        .toDate(),
      chartDataType,
    })
      .then(res => {
        setData(deserialize(res.data.report.data))
        setIsLoadingChartData(false)
      })
      .catch(() => toast.error(t(COMMON_ERRORS[500])))
  }, [community?.id, community?.timezone, yearRangeFunctions, filters.yearOption, chartDataType])

  const handleSelectYearOption = (yearOption) => {
    setFilters(prev => ({ ...prev, yearOption }))
  }

  const growthLabel = useMemo(() => {
    if (!data) {
      return null
    }
    return formatGrowthPercentage(data.studentsAnnualGrowthPercentage)
  }, [data])
  const growthIsNegative = data?.studentsAnnualGrowthPercentage < 0

  return (
    <section className={businessDashboardClasses.widget}>
      <div className={classnames('d-flex justify-content-between align-items-center', businessDashboardClasses.titleWrapper)}>
        <h2 className={businessDashboardClasses.title}>{t('dashboard.business.memberGrowth.memberGrowthWidget.title')}</h2>
        <Select
          id='memberGrowthYearSelect'
          classNames={{
            control: () => classes.yearSelectControl,
            singleValue: () => classes.yearSelectSingleValue,
            menuList: () => classes.yearSelectMenuList,
          }}
          options={yearSelectOptions}
          value={filters.yearOption}
          onChange={handleSelectYearOption}
          isSearchable={false}
        />
      </div>
      <div className='d-flex justify-content-between align-items-center'>
        <div>
          <button
            className={classnames('btn-tab', { active: chartDataType === CHART_DATA_TYPE.students })}
            onClick={() => setChartDataType(CHART_DATA_TYPE.students)}
          >
            {t('dashboard.business.memberGrowth.memberGrowthWidget.studentsTab')}
          </button>
          <button
            className={classnames('btn-tab ml-3', { active: chartDataType === CHART_DATA_TYPE.families })}
            onClick={() => setChartDataType(CHART_DATA_TYPE.families)}
          >
            {t('dashboard.business.memberGrowth.memberGrowthWidget.familiesTab')}
          </button>
        </div>
        {growthLabel && (
          <span className={classnames(classes.growthLabel, { [classes.negative]: growthIsNegative })}>
            {t('dashboard.business.memberGrowth.memberGrowthWidget.growthFromPreviousYear', { percentage: growthLabel })}
          </span>
        )}
      </div>
      {isLoadingChartData ? (
        <div className='flex-grow-1 d-flex justify-content-center align-items-center'>
          <Loader>{t('dashboard.business.memberGrowth.memberGrowthWidget.loadingData')}</Loader>
        </div>
      ) : (
        <>
          <div className={classnames('d-flex justify-content-between', businessDashboardClasses.sectionWrapper)}>
            <SingleStatistics
              value={data.totalStudentsAllTime}
              topLabel={t('dashboard.business.memberGrowth.memberGrowthWidget.totalStudents')}
              bottomLabel={t('dashboard.business.memberGrowth.memberGrowthWidget.allTime')}
            />
            <SingleStatistics
              value={data.newStudentsInPeriod}
              topLabel={t('dashboard.business.memberGrowth.memberGrowthWidget.newStudents')}
              bottomLabel={t('dashboard.business.memberGrowth.memberGrowthWidget.inYear', { year: filters.yearOption.value })}
            />
            <SingleStatistics
              value={data.totalFamiliesAllTime}
              topLabel={t('dashboard.business.memberGrowth.memberGrowthWidget.totalFamilies')}
              bottomLabel={t('dashboard.business.memberGrowth.memberGrowthWidget.allTime')}
            />
            <SingleStatistics
              value={data.newFamiliesInPeriod}
              topLabel={t('dashboard.business.memberGrowth.memberGrowthWidget.newFamilies')}
              bottomLabel={t('dashboard.business.memberGrowth.memberGrowthWidget.inYear', { year: filters.yearOption.value })}
            />
          </div>
          <div className={classes.checkboxRow}>
            <CheckBox
              id='memberGrowthShowOnlyNew'
              label={t('dashboard.business.memberGrowth.memberGrowthWidget.showOnlyNewMembers')}
              isSelected={showOnlyNew}
              onClick={() => setShowOnlyNew(prev => !prev)}
            />
            <div className={classes.legend}>
              <span className={classes.legendItem}>
                <span className={classnames(classes.legendDot, classes.orange)} />
                {chartDataType === CHART_DATA_TYPE.students
                  ? t('dashboard.business.memberGrowth.memberGrowthWidget.legendNewStudents')
                  : t('dashboard.business.memberGrowth.memberGrowthWidget.legendNewFamilies')}
              </span>
              {!showOnlyNew && (
                <span className={classes.legendItem}>
                  <span className={classnames(classes.legendDot, classes.slateGray)} />
                  {chartDataType === CHART_DATA_TYPE.students
                    ? t('dashboard.business.memberGrowth.memberGrowthWidget.legendExistingStudents')
                    : t('dashboard.business.memberGrowth.memberGrowthWidget.legendExistingFamilies')}
                </span>
              )}
            </div>
          </div>
          <div className={businessDashboardClasses.chartWrapper}>
            <MemberGrowthChart
              monthlyData={data.monthlyData}
              chartDataType={chartDataType}
              showOnlyNew={showOnlyNew}
            />
          </div>
        </>
      )}
    </section>
  )
}

export default MemberGrowthWidget
